import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

interface PillFilterGroupProps<T extends string> {
    align?: "left" | "center";
    label: string;
    options: readonly T[];
    renderLabel?: (option: T) => string;
    showContainer?: boolean;
    value: T;
    onChange: (value: T) => void;
}

export default function PillFilterGroup<T extends string>({
    align = "left",
    label,
    options,
    renderLabel,
    showContainer = true,
    value,
    onChange,
}: PillFilterGroupProps<T>) {
    return (
        <Box sx={{ textAlign: align === "center" ? "center" : "left" }}>
            <Typography
                variant="overline"
                color="primary.main"
                sx={{ display: "block", mb: 0.75 }}
            >
                {label}
            </Typography>

            <ToggleButtonGroup
                exclusive
                value={value}
                onChange={(_, nextValue: T | null) => {
                    if (nextValue !== null) {
                        onChange(nextValue);
                    }
                }}
                sx={{
                    width: "100%",
                    display: "inline-flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    justifyContent: align === "center" ? "center" : "flex-start",
                    p: showContainer ? 0.5 : 0,
                    borderRadius: showContainer ? 999 : 0,
                    border: showContainer ? "1px solid rgba(216, 225, 235, 0.9)" : "none",
                    backgroundColor: showContainer ? "rgba(255, 255, 255, 0.68)" : "transparent",
                    maxWidth: "100%",
                    "& .MuiToggleButtonGroup-grouped": {
                        m: 0,
                        minWidth: "fit-content",
                        px: { xs: 1.1, sm: 1.4 },
                        py: 1,
                        border: "none",
                        borderRadius: 999,
                        color: "text.secondary",
                        fontSize: { xs: "0.74rem", sm: "0.82rem" },
                        fontWeight: 700,
                        lineHeight: 1.2,
                        textTransform: "none",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        backgroundColor: "rgba(255, 253, 248, 0.92)",
                    },
                    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                        ml: 0,
                        borderLeft: "none",
                    },
                    "& .Mui-selected": {
                        backgroundColor: "#0f766e !important", // Explicit green override
                        color: "#fff !important",
                        boxShadow: "0 10px 20px rgba(15, 118, 110, 0.18)",
                        transition: "background 0.2s, color 0.2s",
                    },
                    "& .Mui-selected:hover": {
                        backgroundColor: "#115e59 !important", // Darker green on hover
                    },
                }}
            >
                {options.map((option) => (
                    <ToggleButton key={option} value={option} aria-label={`${label}-${option}`}>
                        {renderLabel ? renderLabel(option) : option}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );
}
